import { Component, Input, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { CommonModule } from "@angular/common";

import { LinkComponent } from "@vg-constellation/angular-17/link";
import {
  RadioGroupComponent,
  RadioComponent,
  RadioModule,
} from "@vg-constellation/angular-17/radio";

import * as AccountOwnerConstants from "@services/content/guided-flow/account-owner-content";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { FormValidationService } from "@services/form-validation/form-validation.service";
import { FormsModule } from "@angular/forms";
import { AccountOwnerType } from "../../types/AccountOwnerType";
import { AccountType } from "../../types/AccountType";
import { ACCOUNT_OWNER_SECTION_DESC } from "@services/content/guided-flow/account-owner-content";
import * as GuidedFlowConstants from "@services/content/guided-flow/guided-flow-content";
import { GatekeeperService } from "@weps/shared-http";

const ACCOUNT_OWNER_OPTIONS = [
  {
    label: AccountOwnerConstants.ACCOUNT_OWNER_INDIVIDUAL_BY_NAME,
    desc: AccountOwnerConstants.ACCOUNT_OWNER_INDIVIDUAL_BY_NAME_DESC,
    id: AccountOwnerType.INDIVIDUAL_BY_NAME,
    value: AccountOwnerType.INDIVIDUAL_BY_NAME,
  },
  {
    label: AccountOwnerConstants.ACCOUNT_OWNER_TRUST,
    desc: AccountOwnerConstants.ACCOUNT_OWNER_TRUST_DESC,
    id: AccountOwnerType.TRUST,
    value: AccountOwnerType.TRUST,
  },
  // TODO: Uncomment these when we need these back
  // {
  //   label: AccountOwnerConstants.ACCOUNT_OWNER_BUSINESS,
  //   desc: AccountOwnerConstants.ACCOUNT_OWNER_BUSINESS_DESC,
  //   id: AccountOwnerType.BUSINESS,
  //   value: AccountOwnerType.BUSINESS
  // },
  // {
  //   label: AccountOwnerConstants.ACCOUNT_OWNER_CHARITY,
  //   desc: AccountOwnerConstants.ACCOUNT_OWNER_CHARITY_DESC,
  //   id: AccountOwnerType.CHARITY,
  //   value: AccountOwnerType.CHARITY
  // },
  //{
  //   label: AccountOwnerConstants.ACCOUNT_OWNER_NOT_SURE,
  //   desc: AccountOwnerConstants.ACCOUNT_OWNER_NOT_SURE_DESC,
  //   id: AccountOwnerType.NOT_SURE,
  //   value: AccountOwnerType.NOT_SURE
  // },
];

const GUIDED_FLOW_TITLE_MAP = {
  // WMPS-4245: Theses AccountType should be replaced with InventoryItemType (and the other refs to it in this class)
  [AccountType.BANK]: GuidedFlowConstants.GUIDED_FLOW_ADD_BANK_ACCOUNT,
  [AccountType.RETIREMENT_ANNUITY]:
    GuidedFlowConstants.GUIDED_FLOW_ADD_RETIREMENT_ACCOUNT_OR_ANNUITY,
  [AccountType.INVESTMENT]: GuidedFlowConstants.GUIDED_FLOW_ADD_INVESTMENT_ACCOUNT,
  [AccountType.REAL_ESTATE]: GuidedFlowConstants.GUIDED_FLOW_ADD_REAL_ESTATE,
  [AccountType.LIABILITIES]: GuidedFlowConstants.GUIDED_FLOW_ADD_LIABILITY,
  [AccountType.TRUST]: GuidedFlowConstants.GUIDED_FLOW_ADD_TRUST,
};

@Component({
  selector: "app-account-owner",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LinkComponent,
    RadioGroupComponent,
    RadioComponent,
    RadioModule,
  ],
  templateUrl: "./account-owner.component.html",
  styleUrl: "./account-owner.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class AccountOwnerComponent implements OnInit {
  readonly globalStore = inject(GlobalStateStore);
  readonly formValidationService = inject(FormValidationService);
  // WMPS-4245: This accountType should be replaced with inventoryItemType (and the other refs to it in this class)
  @Input() accountType: AccountType;
  // The value of currentStep is defaulted to 1 inside the current-step-service; so don't really need to set a value here, just initialize variable
  currentStep: number;
  selectedAccountOwnerType: AccountOwnerType | undefined;
  accountOwnerDescription: string;
  hasError: boolean = false;
  isTrustOwnerToggleEnabled: boolean = false;

  constructor(protected gatekeeperService: GatekeeperService) {}

  ngOnInit(): void {
    this.setState();
    this.setAccountOwnerDescription();
    // The value of currentStep is defaulted to 1 inside the current-step-service
    this.currentStep = this.globalStore.getCurrentStep(this.accountType);
    this.gatekeeperService.getFeatures().subscribe((features) => {
      const trustAsOwnerFeature = features.find((f) => f.featureId === "ENABLE_TRUST_AS_OWNER");
      this.isTrustOwnerToggleEnabled = trustAsOwnerFeature?.enabled ?? false;

      // Filter options after feature flags are loaded
      this.filterAccountOwnerOptions();
    });
  }

  // return true if there is an error, to prevent continue
  onSubmit(): boolean {
    const validationResult = this.formValidationService.validateAccountOwner(
      this.selectedAccountOwnerType,
      this.accountType,
    );
    this.hasError = validationResult.hasError;
    return this.hasError;
  }

  handleSelectEvent(event: any): void {
    this.selectedAccountOwnerType = event.inputId;

    // Store the selected account owner in the temp state object
    this.globalStore.updateGuidedFlowTempState(this.accountType, {
      selectedAccountOwnerType: this.selectedAccountOwnerType,
    });
  }

  setState(): void {
    // This will restore the existing state in the case where the user navigates back to this step
    const existingState = this.globalStore.getGuidedFlowState(this.accountType);

    this.selectedAccountOwnerType = existingState?.selectedAccountOwnerType;
    this.globalStore.updateGuidedFlowTempState(this.accountType, {
      onSubmit: this.onSubmit.bind(this),
    });
  }

  filterAccountOwnerOptions(): void {
    const availableOwnerTypes = this.formValidationService.getAvailableAccountOwnerTypes(
      this.accountType,
    );
    this.accountOwnerOptions = ACCOUNT_OWNER_OPTIONS.filter((option) => {
      // Include if it's in the available types for this account type
      const isAvailableForAccountType = availableOwnerTypes.includes(option.id);

      // Special handling for Trust: only include if feature flag is enabled
      if (option.id === AccountOwnerType.TRUST) {
        return isAvailableForAccountType && this.isTrustOwnerToggleEnabled;
      }

      return isAvailableForAccountType;
    });
  }

  setAccountOwnerDescription(): void {
    // WMPS-4245: This accountType should be replaced with inventoryItemType (and the other refs to it in this class)
    const relatedInventoryType: string = GUIDED_FLOW_TITLE_MAP[this.accountType];

    // Doing it this way so I don't have to create 8 more constants that would be similar to other ones we already have
    // Checking if it only starts with "a" isn't good because what if we have an inventoryItemType that starts with the letter a in the future, so checking for the space after as well for safety
    if (relatedInventoryType[0] === "a" && relatedInventoryType[1] === " ") {
      this.accountOwnerDescription = ACCOUNT_OWNER_SECTION_DESC(relatedInventoryType.slice(2));
    } else {
      this.accountOwnerDescription = ACCOUNT_OWNER_SECTION_DESC(relatedInventoryType);
    }
  }

  // WMPS-4245: These should be updated to something more generic than account owner
  protected readonly constants = AccountOwnerConstants;
  // Initialize with INDIVIDUAL_BY_NAME (always available), Trust added after feature flags load
  accountOwnerOptions: typeof ACCOUNT_OWNER_OPTIONS = [ACCOUNT_OWNER_OPTIONS[0]];
  protected readonly AccountOwnerType = AccountOwnerType;
}
